import React, { useState } from "react";
import { useAuth } from "../AuthContext";
import { HiMicrophone, HiPlay, HiLightningBolt, HiGlobe, HiLockClosed, HiAdjustments, HiX } from "react-icons/hi";
import { FiStar, FiHeart, FiGithub, FiBell } from "react-icons/fi";
import { MdOutlinePodcasts, MdOutlineVideoLibrary, MdOutlineSchool, MdSupportAgent, MdOutlineCampaign, MdOutlineSmartToy } from "react-icons/md";
import { VOICE_DATABASE } from "../voices";

const VOICES = {
  female:[
    {name:"Lessac",   g:"f", meta:"American Female · High Quality", tags:["Soft","Warm","Natural"],    val:"Female_Soft_01"},
    {name:"Amy",      g:"f", meta:"American Female · Natural",       tags:["Warm","Gentle"],            val:"Amy_Natural"},
    {name:"HFC",      g:"f", meta:"American Female · PRO",           tags:["Most Human-like","Premium"],val:"HFC_Female_Natural"},
    {name:"Jenny",    g:"f", meta:"British Female",                  tags:["Elegant","British"],        val:"Jenny_Natural"},
  ],
  male:[
    {name:"Ryan",     g:"m", meta:"American Male · High Quality",    tags:["Clear","Smooth"],           val:"Male_Normal_01"},
    {name:"Joe",      g:"m", meta:"American Male · Natural",         tags:["Friendly","Natural"],       val:"Joe_Natural"},
    {name:"HFC",      g:"m", meta:"American Male · PRO",             tags:["Most Human-like","Premium"],val:"HFC_Male_Natural"},
    {name:"Ethan",    g:"m", meta:"Young American Male · 20s",       tags:["Young","Fresh","Energetic"],val:"Young_Boy_01"},
  ]
};

const ALL_VOICES = [...VOICES.female, ...VOICES.male];

export default function Home({ onNavigate, setPreset }) {
  const { user, logout } = useAuth();
  const [favorites, setFavorites] = useState({});
  const [launchSoon, setLaunchSoon] = useState(false);

  const toggleFavorite = (val) => {
    setFavorites(prev => ({ ...prev, [val]: !prev[val] }));
  };

  return (
    <div className="page-wrap">
      
      {/* ── NAVBAR ── */}
      <nav className="nav">
        <div className="nav-brand">
          <div className="nav-logo"><HiMicrophone size={16}/></div>
          <span className="nav-name">VoiceForge</span>
        </div>
        <div className="nav-links">
          <button className="nav-link" onClick={()=>onNavigate("studio")}>Studio</button>
          <a className="nav-link" href="#features">Features</a>
          <a className="nav-link" href="#voices">Voices</a>
          <a className="nav-link" href="#usecases">Use Cases</a>
          <button className="nav-link" onClick={()=>onNavigate("support")}>Support</button>
        </div>
        <div className="nav-end">
          <button className="nav-icon-btn" title="Notifications"><FiBell size={14}/></button>
          <a href="https://github.com/fahadalimazari" target="_blank" rel="noreferrer" className="nav-icon-btn" title="GitHub"><FiGithub size={14}/></a>
          {user && (
            <div className="nav-avatar-wrap">
              {user.picture && <img src={user.picture} alt={user.name} className="nav-avatar"/>}
              <button onClick={logout} style={{fontSize:11,color:"var(--t3)",padding:"4px 10px",borderRadius:6,border:"1px solid var(--border)",background:"rgba(255,255,255,.04)",cursor:"pointer"}} title="Sign out">Sign out</button>
            </div>
          )}
          <button className="nav-cta" onClick={()=>setLaunchSoon(true)}>Launch App</button>
        </div>
      </nav>

      {/* ── HERO BANNER ── */}
      <div className="hero-banner">
        <div className="hero-glow-blob"/>
        <div className="hero-content">
          <div className="hero-badge"><span className="hero-dot"></span>VoiceForge AI Voice Engine v1.2 — Live in Cloud</div>
          <h1 className="hero-h1">The most realistic <span className="hero-gr">AI voice generator</span></h1>
          <p className="hero-sub">Transform text into natural, human‑sounding speech in seconds. {VOICE_DATABASE.length} premium voices, voice cloning, 16 languages — completely private, powered by cloud AI.</p>
          
          <div className="hero-ctas">
            <button onClick={()=>onNavigate("studio")} className="nav-cta" style={{padding:"14px 32px", fontSize:"15px"}}>Launch Studio Console</button>
            <a href="#voices" className="nav-link" style={{fontSize:14, marginLeft:16}}>Browse Library →</a>
          </div>

          {/* Animated voice waveform simulation */}
          <div className="hero-waveform">
            {[14,24,18,34,42,28,16,22,38,46,30,18,24,36,44,28,14,20,38,42,24,14].map((h, i) => (
              <div key={i} className="wave-bar" style={{height: h, animationDelay: `${i*0.06}s`}}/>
            ))}
          </div>

          <div className="hero-nums">
            <div className="hnum"><div className="hnum-n">{VOICE_DATABASE.length}<em>+</em></div><div className="hnum-l">Voices</div></div>
            <div className="hnum"><div className="hnum-n">16<em>+</em></div><div className="hnum-l">Languages</div></div>
            <div className="hnum"><div className="hnum-n">30<em>s</em></div><div className="hnum-l">Generation</div></div>
            <div className="hnum"><div className="hnum-n">100<em>%</em></div><div className="hnum-l">Private</div></div>
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <section className="section section-alt" id="features">
        <div className="s-ey">Features</div>
        <h2 className="s-h">Everything you need for great audio</h2>
        <p className="s-d">A complete AI voice toolkit — zero compromise on quality or privacy.</p>
        <div className="feat-g">
          {[
            {i:<HiMicrophone size={18}/>,h:`${VOICE_DATABASE.length} Premium Voices`,p:"A comprehensive library of professional voices - American, British and international accents - trained on real human speech."},
            {i:<HiLightningBolt size={18}/>,h:"Under 30s Generation",p:"Real-time processing. Studio-quality WAV audio in seconds with zero queue delays."},
            {i:<HiMicrophone size={18}/>,h:"Voice Cloning",p:"Upload any 5–30 sec clip. AI matches pitch, tone, and rhythm for a near-perfect clone."},
            {i:<HiGlobe size={18}/>,h:"16 Languages",p:"English, Spanish, French, German, Arabic, Hindi, Chinese, Japanese, Korean, Russian and more."},
            {i:<HiLockClosed size={18}/>,h:"100% Private & Secure",p:"Your voice data is processed securely in the cloud and never stored or shared — zero data collection."},
            {i:<HiAdjustments size={18}/>,h:"Pitch & Speed Control",p:"Adjust pitch ±3 semitones, 4 speed presets. Download crystal-clear WAV files instantly."},
          ].map((f, i)=>(
            <div key={i} className="feat-c">
              <div className="feat-i">{f.i}</div>
              <div className="feat-h">
                {f.h}
                {f.h === "Voice Cloning" && (
                  <span style={{
                    marginLeft: "8px",
                    fontSize: "10px",
                    background: "rgba(239, 68, 68, 0.2)",
                    color: "#f87171",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    display: "inline-block",
                    verticalAlign: "middle"
                  }}>Coming Soon</span>
                )}
              </div>
              <p className="feat-p">{f.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── VOICES SHOWCASE ── */}
      <section className="section" id="voices">
        <div className="s-ey">Voice Library</div>
        <h2 className="s-h">{VOICE_DATABASE.length} voices that sound truly human</h2>
        <p className="s-d">Each voice is trained on thousands of hours of real speech. Click any voice to use it in the studio above.</p>
        <div className="vshow-g">
          {ALL_VOICES.map((v,i)=>(
            <div key={i} className="vshow-c" style={{"--vc":v.g==="f"?"#f43f5e":"#38bdf8"}}>
              <div className="vshow-av-wrap" style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
                <div className={`vshow-av ${v.g==="f"?"vav-f":"vav-m"}`}>{v.g==="f"?"♀":"♂"}</div>
                <button onClick={()=>toggleFavorite(v.val)} style={{background:"transparent",border:"none",cursor:"pointer",color:favorites[v.val]?"#fbbf24":"var(--t3)"}}>
                  <FiHeart size={16} fill={favorites[v.val]?"currentColor":"none"}/>
                </button>
              </div>
              <div className="vshow-name">{v.name} <span style={{fontSize:9,fontWeight:700,color:"var(--p3)",marginLeft:6,background:"rgba(99,102,241,0.08)",padding:"2px 6px",borderRadius:100}}>PRO</span></div>
              <div className="vshow-meta">{v.meta}</div>
              <div className="vshow-tags">{v.tags.map(t=><span key={t} className="vshow-tag">{t}</span>)}</div>
              
              <button style={{background:"rgba(255,255,255,0.02)",border:"1px solid var(--border)",borderRadius:8,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,fontSize:12,fontWeight:600,color:"#fff",padding:"8px 16px",marginTop:"auto",width:"100%"}}
                onClick={()=>{setPreset(v.val);onNavigate("studio");}}>
                <HiPlay size={12}/> Try in Studio
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section className="section section-alt" id="usecases">
        <div className="s-ey">Use Cases</div>
        <h2 className="s-h">Built for every creator</h2>
        <p className="s-d">From indie podcasters to enterprise developers — VoiceForge delivers professional results every time.</p>
        <div className="uc-g">
          {[
            {i:<MdOutlinePodcasts size={20}/>,h:"Podcasts",        p:"Professional intros, outros, and transitions without any recording equipment."},
            {i:<MdOutlineVideoLibrary size={20}/>,h:"Video Narration", p:"Studio voiceovers for tutorials, courses, and presentations in seconds."},
            {i:<MdOutlineSchool size={20}/>,h:"E-Learning",       p:"Clear, natural narration in 16 languages for global learners."},
            {i:<MdSupportAgent size={20}/>,h:"IVR & Telephony",   p:"Professional phone system messages that sound human and welcoming."},
            {i:<MdOutlineCampaign size={20}/>,h:"Marketing",         p:"Compelling ad scripts with voice that converts. Test multiple styles fast."},
            {i:<MdOutlineSmartToy size={20}/>,h:"AI Products",       p:"Add natural voice to chatbots, assistants, and AI apps instantly."},
          ].map((u, i)=><div key={i} className="uc-c"><div className="uc-i">{u.i}</div><div className="uc-h">{u.h}</div><p className="uc-p">{u.p}</p></div>)}
        </div>
      </section>

      {/* ── TESTIMONIALS (Marquee) ── */}
      <section className="section">
        <div className="s-ey">Testimonials</div>
        <h2 className="s-h">Trusted by creators worldwide</h2>
        <p className="s-d">Join thousands of podcasters, developers, and marketers using VoiceForge.</p>
        
        <div className="marquee-container">
          <div className="marquee-content">
            {[
              {t:"\"The most natural-sounding AI voices I've heard. My podcast intros sound completely professional now.\"",n:"Sarah K.",r:"Podcast Creator"},
              {t:"\"Voice cloning is incredible — I uploaded a 10-second clip and the output matched perfectly.\"",n:"Ahmad R.",r:"Content Developer"},
              {t:"\"Cloud-powered and completely secure. The quality is indistinguishable from real voice.\"",n:"Marcus L.",r:"Marketing Director"},
              {t:"\"The speed is incredible. Less than 20 seconds to generate a full minute of narration.\"",n:"John D.",r:"Video Editor"},
            ].map((t, idx)=>(
              <div key={idx} className="t-c">
                <div className="t-stars">{[1,2,3,4,5].map(i=><FiStar key={i} size={12} fill="#fbbf24" stroke="none"/>)}</div>
                <p className="t-txt">{t.t}</p>
                <div className="t-author">
                  <div className="t-av">{t.n[0]}</div>
                  <div>
                    <div className="t-name">{t.n}</div>
                    <div className="t-role">{t.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-s">
        <h2>Start creating your voice today</h2>
        <p>No account needed. No subscription. Open the Voice Studio and generate professional voiceovers in seconds — powered by our secure cloud AI engine.</p>
        <button onClick={()=>onNavigate("studio")} className="nav-cta" style={{padding:"14px 32px", fontSize:"15px", border:"none", cursor:"pointer"}}>
          Launch Voice Studio
        </button>
      </div>

      {/* ── FOOTER (Vercel-like) ── */}
      <footer className="footer">
        <div className="footer-in">
          <div className="ft-top">
            <div>
              <div className="ft-logo"><div className="nav-logo" style={{width:24,height:24}}><HiMicrophone size={12}/></div><span style={{fontWeight:850,marginLeft:8}}>VoiceForge AI</span></div>
              <p className="ft-desc">Studio-quality AI voice generation powered by our cloud infrastructure. Fast, private, and always free.</p>
              <div className="ft-tags"><span className="ft-tag">Piper TTS</span><span className="ft-tag">Node.js</span><span className="ft-tag">React</span><span className="ft-tag">Cloud AI</span></div>
            </div>
            <div>
              <div className="fc-t">Studio</div>
              <button className="fc-l" style={{background:"transparent",border:"none",cursor:"pointer",textAlign:"left"}} onClick={()=>onNavigate("studio")}>Voice Studio</button>
              <a className="fc-l" href="#voices">Voice Library</a>
              <a className="fc-l" href="#features">Features</a>
              <a className="fc-l" href="#usecases">Use Cases</a>
            </div>
            <div>
              <div className="fc-t">Company</div>
              <span className="fc-l">About Us</span>
              <span className="fc-l">Privacy Policy</span>
              <span className="fc-l">Terms of Service</span>
              <span className="fc-l">Security</span>
            </div>
            <div>
              <div className="fc-t">Resources</div>
              <a className="fc-l" href="https://github.com/rhasspy/piper" target="_blank" rel="noreferrer">Piper Engine</a>
              <span className="fc-l">Cloud Processing</span>
              <span className="fc-l">API Documentation</span>
              <span className="fc-l">FAQ & Help</span>
            </div>
          </div>
          <div className="ft-bottom">
            <span>© 2024 VoiceForge AI — Developed by <a href="https://github.com/fahadalimazari" target="_blank" rel="noreferrer" style={{color:"#8B5CF6", textDecoration:"none", fontWeight:"600"}}>Fahad Ali Mazari</a></span>
            <span>Powered by Piper TTS · Cloud AI Engine · Zero data collection</span>
          </div>
        </div>
      </footer>

      {launchSoon && (
        <div className="settings-overlay" onClick={()=>setLaunchSoon(false)}>
          <div className="settings-modal" style={{
            maxWidth: 550,
            height: "auto",
            padding: 36,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            border: "1.5px solid rgba(244, 63, 94, 0.3)",
            boxShadow: "0 30px 70px rgba(0,0,0,0.9), 0 0 40px rgba(244, 63, 94, 0.15)",
            borderRadius: 24,
            background: "rgba(11, 11, 24, 0.98)"
          }} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setLaunchSoon(false)} style={{
              position: "absolute",
              top: 20,
              right: 20,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#808090",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 4
            }} title="Close"><HiX size={16}/></button>

            <div style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(244, 63, 94, 0.1)",
              border: "1px solid rgba(244, 63, 94, 0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--rose)",
              marginBottom: 20,
              fontSize: 24,
              fontWeight: 700
            }}>⚠️</div>
            
            <h3 style={{fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 12}}>App Launch Coming Soon</h3>
            
            <p style={{fontSize: 14.5, color: "#B4B4C5", maxWidth: 440, lineHeight: 1.6, marginBottom: 24}}>
              The VoiceForge Studio App is currently undergoing final optimization tests. Local model integrations, real-time wave synthesis, and premium developer studio consoles will be available in the upcoming build!
            </p>
            
            <button className="settings-tab-btn active" onClick={()=>setLaunchSoon(false)} style={{width: "auto", padding: "10px 24px", borderRadius: 12, fontWeight: 700, background: "rgba(244, 63, 94, 0.15)", borderColor: "rgba(244, 63, 94, 0.3)", color: "var(--rose)", cursor: "pointer"}}>
              Dismiss Warning
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
